import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isServicesActive = location.pathname.startsWith('/ydelser');

  const logoUrl = "https://assets.auxodev.dk/assets/933ba291-d562-4801-b20a-fae6de561b45/logo.png";

  return (
    <header className={`sticky top-0 z-40 bg-white text-gray-900 border-b border-gray-200 overflow-x-clip transition-all duration-300 ${
      isScrolled ? 'shadow-md py-0' : 'shadow-sm py-1'
    }`}>
      {/* Main Header Bar Container */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${
        isScrolled ? 'pt-1.5 pb-1' : 'pt-3 pb-2'
      }`}>
        {/* Top Row: Logo & Top Right Actions */}
        <div className="flex items-center justify-between">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none py-1 border-0 border-none outline-none ring-0 shadow-none">
            <img 
              src={logoUrl} 
              alt="Fyns - Tømreren ApS logo – Professionelt tømrer- og snedkerarbejde på Fyn" 
              className={`w-auto max-w-[280px] sm:max-w-[480px] object-contain border-0 border-none outline-none ring-0 shadow-none bg-transparent transition-all duration-300 ${
                isScrolled ? 'h-16 sm:h-20 lg:h-24' : 'h-20 sm:h-28 lg:h-32'
              }`}
            />
          </Link>

          {/* Desktop Right CTA / Phone */}
          <div className="hidden lg:flex items-center gap-5">
            <a href="tel:60715018" className="text-sm font-semibold text-gray-800 hover:text-[#3A5A40] flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-phone text-[#3A5A40]"></i>
              <span>Ring: 60 71 50 18</span>
            </a>
            <Link
              to="/kontakt"
              className="bg-[#d75d5a] hover:bg-[#c24b48] text-white px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md inline-flex items-center gap-2"
            >
              Indhent tilbud
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>

          {/* Burger Menu Button - Visible under lg */}
          <div className="lg:hidden flex items-center gap-3">
            <Link
              to="/kontakt"
              className="hidden sm:inline-flex bg-[#d75d5a] hover:bg-[#c24b48] text-white px-3.5 py-2 text-xs font-semibold transition-all duration-300 hover:-translate-y-1"
            >
              Få tilbud
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-700 hover:text-black focus:outline-none transition-colors"
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}></i>
            </button>
          </div>

        </div>
      </div>

      {/* Desktop Navigation Links - Full Left Bleed with Slanted Right Edge */}
      <div className="hidden lg:block relative w-full pb-2 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="relative inline-flex items-center gap-1.5 text-white py-2 pl-2 pr-12">
            {/* Dark background with full left bleed and slanted right edge */}
            <div 
              className="absolute top-0 bottom-0 left-[-3000px] right-0 bg-[#232323] shadow-md pointer-events-none"
              style={{
                clipPath: 'polygon(0 0, 100% 0, calc(100% - 28px) 100%, 0 100%)'
              }}
            />

            <Link
              to="/"
              className={`relative z-10 px-4 py-1.5 text-sm font-semibold transition-all duration-200 text-white hover:text-[#d75d5a] rounded-sm ${
                isActive('/') && location.pathname === '/'
                  ? 'text-amber-400 font-bold'
                  : ''
              }`}
            >
              Forside
            </Link>

            {/* Ydelser Dropdown */}
            <div className="relative group z-20">
              <Link
                to="/ydelser"
                className={`px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 text-white hover:text-[#d75d5a] rounded-sm transition-colors duration-200 cursor-pointer ${
                  isServicesActive
                    ? 'text-amber-400 font-bold'
                    : ''
                }`}
              >
                Ydelser
                <i className="fa-solid fa-chevron-down text-xs transition-transform duration-200 group-hover:rotate-180 text-white group-hover:text-[#d75d5a]"></i>
              </Link>

              {/* Dropdown Menu Container with seamless hover zone */}
              <div className="absolute left-0 top-full pt-1.5 w-60 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-50">
                <div className="bg-[#232323] text-white border border-gray-700 shadow-2xl py-2 rounded-b-md overflow-hidden">
                  <Link
                    to="/ydelser/tag-facade"
                    className={`block px-4 py-2.5 text-sm transition-colors text-white hover:text-[#d75d5a] hover:bg-white/10 ${
                      location.pathname === '/ydelser/tag-facade'
                        ? 'font-bold text-amber-400'
                        : ''
                    }`}
                  >
                    Tag & Facade
                  </Link>
                  <Link
                    to="/ydelser/interior-gulve"
                    className={`block px-4 py-2.5 text-sm transition-colors text-white hover:text-[#d75d5a] hover:bg-white/10 ${
                      location.pathname === '/ydelser/interior-gulve'
                        ? 'font-bold text-amber-400'
                        : ''
                    }`}
                  >
                    Interiør & Gulve
                  </Link>
                  <Link
                    to="/ydelser/koekken-inventar"
                    className={`block px-4 py-2.5 text-sm transition-colors text-white hover:text-[#d75d5a] hover:bg-white/10 ${
                      location.pathname === '/ydelser/koekken-inventar'
                        ? 'font-bold text-amber-400'
                        : ''
                    }`}
                  >
                    Køkken & Inventar
                  </Link>
                  <Link
                    to="/ydelser/asbestsanering"
                    className={`block px-4 py-2.5 text-sm transition-colors text-white hover:text-[#d75d5a] hover:bg-white/10 ${
                      location.pathname === '/ydelser/asbestsanering'
                        ? 'font-bold text-amber-400'
                        : ''
                    }`}
                  >
                    Autoriseret Asbestsanering
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/galleri"
              className={`relative z-10 px-4 py-1.5 text-sm font-semibold transition-all duration-200 text-white hover:text-[#d75d5a] rounded-sm ${
                isActive('/galleri')
                  ? 'text-amber-400 font-bold'
                  : ''
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/om-os"
              className={`relative z-10 px-4 py-1.5 text-sm font-semibold transition-all duration-200 text-white hover:text-[#d75d5a] rounded-sm ${
                isActive('/om-os')
                  ? 'text-amber-400 font-bold'
                  : ''
              }`}
            >
              Om os
            </Link>

            <Link
              to="/kontakt"
              className={`relative z-10 px-4 py-1.5 text-sm font-semibold transition-all duration-200 text-white hover:text-[#d75d5a] rounded-sm ${
                isActive('/kontakt')
                  ? 'text-amber-400 font-bold'
                  : ''
              }`}
            >
              Kontakt
            </Link>
          </nav>
        </div>
      </div>

      {/* Mobile / Tablet Drawer Menu (< lg) */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#232323] text-white border-t border-gray-800 px-4 pt-3 pb-6 space-y-2 animate-fadeIn shadow-lg">
          <Link
            to="/"
            className={`block px-3 py-2.5 text-base font-medium text-white transition-colors hover:bg-white/10 ${
              isActive('/') && location.pathname === '/'
                ? 'font-bold text-amber-400'
                : ''
            }`}
          >
            Forside
          </Link>

          <div>
            <button
              onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
              className={`w-full text-left px-3 py-2.5 text-base font-medium text-white flex justify-between items-center transition-colors hover:bg-white/10 ${
                isServicesActive ? 'font-bold text-amber-400' : ''
              }`}
            >
              <span>Ydelser</span>
              <i className={`fa-solid fa-chevron-down text-xs text-white transition-transform ${servicesDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {servicesDropdownOpen && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-white/40 my-1">
                <Link
                  to="/ydelser/tag-facade"
                  className="block px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  Tag & Facade
                </Link>
                <Link
                  to="/ydelser/interior-gulve"
                  className="block px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  Interiør & Gulve
                </Link>
                <Link
                  to="/ydelser/koekken-inventar"
                  className="block px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  Køkken & Inventar
                </Link>
                <Link
                  to="/ydelser/asbestsanering"
                  className="block px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  Autoriseret Asbestsanering
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/galleri"
            className={`block px-3 py-2.5 text-base font-medium text-white transition-colors hover:bg-white/10 ${
              isActive('/galleri')
                ? 'font-bold text-amber-400'
                : ''
            }`}
          >
            Galleri
          </Link>

          <Link
            to="/om-os"
            className={`block px-3 py-2.5 text-base font-medium text-white transition-colors hover:bg-white/10 ${
              isActive('/om-os')
                ? 'font-bold text-amber-400'
                : ''
            }`}
          >
            Om os
          </Link>

          <Link
            to="/kontakt"
            className={`block px-3 py-2.5 text-base font-medium text-white transition-colors hover:bg-white/10 ${
              isActive('/kontakt')
                ? 'font-bold text-amber-400'
                : ''
            }`}
          >
            Kontakt
          </Link>

          <div className="pt-4 border-t border-gray-800 mt-2 space-y-3">
            <Link
              to="/kontakt"
              className="block w-full text-center bg-[#d75d5a] hover:bg-[#c24b48] text-white py-3 font-semibold text-base transition-colors shadow-sm"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:60715018"
              className="block w-full text-center bg-white/10 hover:bg-white/20 text-white py-3 font-semibold text-base transition-colors whitespace-nowrap border border-white/20"
            >
              <i className="fa-solid fa-phone text-white mr-2"></i>
              60 71 50 18
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
